/**
 * features/events/api.ts — queries para Eventos (lista + detalle + categorias).
 *
 * Hooks tipados sobre el singleton `supabase` envueltos en `useQuery`.
 * Filtros segun spec PROMPT_MAESTRO.md §4.2 "Eventos lista":
 *  - `status='upcoming'` => `ends_at >= now()` y `status='upcoming'` (incluye los
 *    eventos en curso, CEC-261009-8e6d). Orden asc por inicio.
 *  - `status='past'`     => `ends_at < now()` OR `status='past'`. Orden desc.
 *  - `status='all'`      => sin filtro de fecha/estado. Orden asc.
 *  - `kind`              => filtra `events.kind` exacto (ceremonia / taller / experiencia / ritual).
 *  - `categorySlug`      => primero resuelve `event_categories.id` por slug y luego filtra.
 *
 * `queryKey` segmentada por filtros para que F6 (admin) pueda invalidar bloques
 * especificos cuando publique / despublique eventos.
 *
 * Reglas:
 *  - Solo columnas que la UI consume.
 *  - Filtra `is_published = true` + `deleted_at is null` en publicas.
 *  - Tipos derivados de `Database['public']['Tables']['events']['Row']`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { supabase } from '../../lib/supabase/client';
import type { Database } from '../../lib/supabase/types';

// ---------------------------------------------------------------------------
// Tipos derivados del schema.
// ---------------------------------------------------------------------------

type EventRow = Database['public']['Tables']['events']['Row'];
type EventCategoryRow = Database['public']['Tables']['event_categories']['Row'];

/** Item de agenda — array dentro del jsonb `events.agenda`. */
export interface AgendaItem {
  time: string;
  label: string;
}

/** Evento ya saneado para consumo de UI. */
export interface EventModel {
  id: EventRow['id'];
  title: EventRow['title'];
  slug: EventRow['slug'];
  kind: EventRow['kind'];
  modality: EventRow['modality'];
  starts_at: EventRow['starts_at'];
  ends_at: EventRow['ends_at'];
  capacity: EventRow['capacity'];
  location_name: EventRow['location_name'];
  location_address: EventRow['location_address'];
  city: EventRow['city'];
  summary: EventRow['summary'];
  description_md: EventRow['description_md'];
  /** Items normalizados {time, label}. */
  agenda: AgendaItem[];
  /** Strings (lo que incluye el evento) */
  includes: string[];
  price_cop: EventRow['price_cop'];
  ambient_audio_url: EventRow['ambient_audio_url'];
  ambient_audio_label: EventRow['ambient_audio_label'];
  hero_url: EventRow['hero_url'];
  gallery_urls: EventRow['gallery_urls'];
  video_urls: EventRow['video_urls'];
  status: EventRow['status'];
  category_id: EventRow['category_id'];
  /** Enlace de la categoría (p. ej. `exposiciones`); null si no tiene. */
  category_slug: string | null;
}

/** Categoria de evento. */
export type EventCategory = EventCategoryRow;

/** Filtros aceptados por `useEvents`. */
export interface EventsFilter {
  /** Tipo de evento (kind). */
  kind?: EventModel['kind'];
  /**
   * Ventana temporal:
   *  - `upcoming` (default): eventos futuros con status `upcoming`. Orden asc.
   *  - `past`: eventos pasados o con status `past`. Orden desc.
   *  - `all`: sin filtro. Orden asc.
   */
  status?: 'upcoming' | 'past' | 'all';
  /** Slug de categoria (de `event_categories.slug`). */
  categorySlug?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Columnas seleccionadas para `EventModel`. Compartido por lista + detalle.
const EVENT_SELECT =
  'id, title, slug, kind, modality, starts_at, ends_at, capacity, location_name, location_address, city, summary, description_md, agenda, includes, price_cop, ambient_audio_url, ambient_audio_label, hero_url, gallery_urls, video_urls, status, category_id, event_categories(slug)';

/**
 * Normaliza el campo `includes` desde JSON -> string[]. Defensivo ante seeds
 * que devuelvan null u objetos inesperados.
 */
function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

/**
 * Normaliza el campo `agenda` jsonb -> `AgendaItem[]`. Acepta solo entradas con
 * los campos `time` y `label` como strings.
 */
function asAgenda(value: unknown): AgendaItem[] {
  if (!Array.isArray(value)) return [];
  const items: AgendaItem[] = [];
  for (const it of value) {
    if (
      it &&
      typeof it === 'object' &&
      typeof (it as { time?: unknown }).time === 'string' &&
      typeof (it as { label?: unknown }).label === 'string'
    ) {
      items.push({
        time: (it as { time: string }).time,
        label: (it as { label: string }).label,
      });
    }
  }
  return items;
}

/** Mapea fila cruda a `EventModel`. Garantiza arrays no-null. */
function mapEvent(row: Record<string, unknown>): EventModel {
  return {
    id: row.id as string,
    title: row.title as string,
    slug: row.slug as string,
    kind: row.kind as EventRow['kind'],
    modality: row.modality as EventRow['modality'],
    starts_at: row.starts_at as string,
    ends_at: row.ends_at as string,
    capacity: row.capacity as number,
    location_name: (row.location_name as string | null) ?? null,
    location_address: (row.location_address as string | null) ?? null,
    city: (row.city as string | null) ?? null,
    summary: (row.summary as string | null) ?? null,
    description_md: (row.description_md as string | null) ?? null,
    agenda: asAgenda(row.agenda),
    includes: asStringArray(row.includes),
    price_cop: row.price_cop as number,
    ambient_audio_url: (row.ambient_audio_url as string | null) ?? null,
    ambient_audio_label: (row.ambient_audio_label as string | null) ?? null,
    hero_url: (row.hero_url as string | null) ?? null,
    gallery_urls: (row.gallery_urls as string[] | null) ?? [],
    video_urls: (row.video_urls as string[] | null) ?? [],
    status: row.status as EventRow['status'],
    category_id: (row.category_id as string | null) ?? null,
    category_slug: (row.event_categories as { slug?: string } | null)?.slug ?? null,
  };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Lista eventos publicados. Aplica filtros de spec §4.2.
 *
 * - `status='upcoming'`: `ends_at >= now()` AND `status='upcoming'`. Orden asc.
 * - `status='past'`: `ends_at < now()` OR `status='past'`. Orden desc.
 * - `status='all'`: sin filtros temporales. Orden asc.
 *
 * Si `categorySlug` esta presente, primero resuelve la categoria por slug y
 * filtra por su id.
 */
export function useEvents(filter: EventsFilter = {}): UseQueryResult<EventModel[], Error> {
  const { kind, status = 'upcoming', categorySlug } = filter;
  return useQuery<EventModel[], Error>({
    queryKey: ['events', 'list', status, kind ?? '__any_kind__', categorySlug ?? '__any_cat__'],
    queryFn: async (): Promise<EventModel[]> => {
      // Resolvemos category_id por slug si aplica (1 hop). Evita un join
      // explicito y mantiene la query principal simple.
      let categoryId: string | null = null;
      if (categorySlug) {
        const { data: cat, error: catErr } = await supabase
          .from('event_categories')
          .select('id')
          .eq('slug', categorySlug)
          .is('deleted_at', null)
          .maybeSingle();
        if (catErr) throw new Error(catErr.message);
        if (!cat) return [];
        categoryId = cat.id;
      }

      let q = supabase
        .from('events')
        .select(EVENT_SELECT)
        .eq('is_published', true)
        .is('deleted_at', null);

      // Filtro por kind exacto si se solicita.
      if (kind) q = q.eq('kind', kind);
      if (categoryId) q = q.eq('category_id', categoryId);

      // Filtros de ventana temporal.
      const now = new Date().toISOString();
      if (status === 'upcoming') {
        // Por fecha de FIN: un evento en curso sigue en "Próximos".
        q = q.eq('status', 'upcoming').gte('ends_at', now);
        q = q.order('starts_at', { ascending: true });
      } else if (status === 'past') {
        // `ends_at < now()` OR `status='past'` — Supabase admite `or()` con
        // sintaxis PostgREST. Orden desc por fecha.
        q = q.or(`ends_at.lt.${now},status.eq.past`);
        q = q.order('starts_at', { ascending: false });
      } else {
        q = q.order('starts_at', { ascending: true });
      }

      const { data, error } = await q;
      if (error) throw new Error(error.message);
      return (data ?? []).map((row) => mapEvent(row as Record<string, unknown>));
    },
  });
}

// ---------------------------------------------------------------------------
// Mutation: crear una inscripcion (event_registration). F4.5-events.
// ---------------------------------------------------------------------------

type EventRegistrationRow = Database['public']['Tables']['event_registrations']['Row'];
type EventRegistrationInsert = Database['public']['Tables']['event_registrations']['Insert'];

export interface CreateEventRegistrationInput {
  eventId: string;
  /** Si user logueado, su id; null para guest. */
  customerId: string | null;
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  /**
   * Notas opcionales recolectadas por el wizard. El schema actual NO tiene
   * columna `event_registrations.notes` (ver SCHEMA.md §8.4) por lo que este
   * campo se IGNORA en el insert. Cuando se agregue la columna, el payload
   * la incluira.
   */
  notes?: string;
}

export interface CreatedEventRegistration {
  id: EventRegistrationRow['id'];
  status: EventRegistrationRow['status'];
  event_id: EventRegistrationRow['event_id'];
}

/**
 * Construye el payload de insert respetando el constraint `event_reg_identity_chk`:
 * customer_id != null  XOR  (guest_name + guest_email).
 */
function buildEventRegistrationInsertPayload(
  input: CreateEventRegistrationInput,
): EventRegistrationInsert {
  const isGuest = input.customerId === null;
  return {
    event_id: input.eventId,
    customer_id: input.customerId,
    guest_name: isGuest ? (input.guestName ?? null) : null,
    guest_email: isGuest ? (input.guestEmail ?? null) : null,
    guest_phone: isGuest ? (input.guestPhone ?? null) : null,
    // Decision: incluso si price=0, el status inicial es 'reserved' (no 'paid'),
    // porque no hay un pago real asociado. El admin lo puede marcar 'attended'
    // despues del evento.
    status: 'reserved',
  };
}

/**
 * Mutation: crea una fila en `event_registrations` con `status='reserved'`.
 * Devuelve el `id` para que el wizard lo pase al `<WompiCheckoutButton>`
 * cuando el evento sea de pago, o al `EventRegistrationSuccessState` cuando
 * sea gratuito.
 *
 * Invalida el query de cupos (`event_registrations / count / <eventId>`)
 * tras success para que la UI refleje el decremento inmediatamente.
 */
export function useCreateEventRegistration(): UseMutationResult<
  CreatedEventRegistration,
  Error,
  CreateEventRegistrationInput
> {
  const queryClient = useQueryClient();
  return useMutation<CreatedEventRegistration, Error, CreateEventRegistrationInput>({
    mutationFn: async (input: CreateEventRegistrationInput): Promise<CreatedEventRegistration> => {
      const payload = buildEventRegistrationInsertPayload(input);
      const { data, error } = await supabase
        .from('event_registrations')
        .insert(payload)
        .select('id, status, event_id')
        .single();

      if (error) throw new Error(error.message);
      if (!data) throw new Error('La inscripcion no se pudo crear.');
      return data as CreatedEventRegistration;
    },
    onSuccess: (data) => {
      // Invalida el conteo de cupos del evento para refrescar la UI.
      void queryClient.invalidateQueries({
        queryKey: ['event_registrations', 'count', data.event_id],
      });
    },
  });
}

/** Catalogo de categorias de eventos. */
export function useEventCategories(): UseQueryResult<EventCategory[], Error> {
  return useQuery<EventCategory[], Error>({
    queryKey: ['event_categories'],
    queryFn: async (): Promise<EventCategory[]> => {
      const { data, error } = await supabase
        .from('event_categories')
        .select('*')
        .is('deleted_at', null)
        .order('name', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as EventCategory[];
    },
  });
}

/**
 * Trae un unico evento publicado por slug. `enabled` solo cuando `slug` existe.
 * `data === null` => evento no encontrado (UI debe mostrar EmptyState).
 */
export function useEventBySlug(slug: string | undefined): UseQueryResult<EventModel | null, Error> {
  return useQuery<EventModel | null, Error>({
    queryKey: ['events', 'detail', slug ?? ''],
    enabled: Boolean(slug),
    queryFn: async (): Promise<EventModel | null> => {
      const { data, error } = await supabase
        .from('events')
        .select(EVENT_SELECT)
        .eq('slug', slug as string)
        .eq('is_published', true)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      return mapEvent(data as Record<string, unknown>);
    },
  });
}
